import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { getImageUrlSync, getApiBaseURL } from '../../utils/imageProxy';
import { getCurrencySymbol } from '../../utils/currencies';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';
import { businessCurrency } from '../../utils/businessDefaults';

export interface ProductCardProps {
  product: {
    _id?: string;
    name: string;
    description?: string;
    price: number;
    salePrice?: number;
    currency?: string;
    primaryImage?: string;
    images?: Array<{ url: string; isPrimary?: boolean }>;
    availability?: 'in_stock' | 'out_of_stock' | 'preorder' | 'backorder';
    inventoryQuantity?: number;
    hasVariants?: boolean;
    sku?: string;
    brand?: string;
    catalogueIds?: string[];
    catalogues?: Array<{ _id: string; name: string }>; // Array of catalogues this product belongs to
  };
  onPress?: (productId: string) => void;
  onEdit?: (productId: string) => void;
  onDelete?: (productId: string) => void;
  showActions?: boolean;
  containerStyle?: any;
  /** Multi-select mode (bulk "Add to Vendor"/"Add to Category" actions) —
   * tapping the card toggles selection instead of navigating, and a
   * checkbox renders over the image. */
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (productId: string) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onPress,
  onEdit,
  onDelete,
  showActions = true,
  containerStyle,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);

  // Helper function to get image URL with proxy support
  const getImageUrl = useCallback((url: string): string => {
    return getImageUrlSync(url, apiBaseURL);
  }, [apiBaseURL]);

  const handlePress = () => {
    if (!product._id) return;
    if (selectionMode) {
      onToggleSelect?.(product._id);
    } else {
      onPress?.(product._id);
    }
  };

  const handleEdit = (e: any) => {
    e.stopPropagation();
    if (product._id && onEdit) {
      onEdit(product._id);
    }
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    if (product._id && onDelete) {
      onDelete(product._id);
    }
  };

  // Get primary image
  const primaryImageUrl =
    product.primaryImage ||
    product.images?.find((img) => img.isPrimary)?.url ||
    product.images?.[0]?.url;
  
  const primaryImage = primaryImageUrl ? getImageUrl(primaryImageUrl) : null;

  // Calculate display price
  const displayPrice = product.salePrice || product.price;
  const hasDiscount = product.salePrice && product.salePrice < product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.price - product.salePrice!) / product.price) * 100)
    : 0;

  // Availability status
  const isOutOfStock = product.availability === 'out_of_stock' || (product.inventoryQuantity !== undefined && product.inventoryQuantity <= 0);
  const isLowStock = product.inventoryQuantity !== undefined && product.inventoryQuantity > 0 && product.inventoryQuantity < 10;

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle, selectionMode && isSelected && styles.containerSelected]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        {/* Product Image */}
        <View style={styles.imageContainer}>
          {primaryImage ? (
            <Image source={{ uri: primaryImage }} style={styles.image} />
          ) : (
            <View style={styles.placeholderImage}>
              <Text style={styles.placeholderText}>
                {product.name
                  .split(' ')
                  .map(n => n.charAt(0))
                  .join('')
                  .substring(0, 2)
                  .toUpperCase()}
              </Text>
            </View>
          )}
          
          {/* Discount Badge */}
          {hasDiscount && (
            <View style={styles.discountBadge}>
              <Text style={styles.discountText}>-{discountPercent}%</Text>
            </View>
          )}

          {selectionMode && (
            <View style={styles.selectionCheckbox}>
              <Ionicons
                name={isSelected ? 'checkmark-circle' : 'ellipse-outline'}
                size={22}
                color={isSelected ? colors.primary : colors.white}
              />
            </View>
          )}

          {/* Availability Badge */}
          {isOutOfStock && (
            <View style={styles.outOfStockBadge}>
              <Ionicons name="close-circle" size={14} color={colors.white} />
              <Text style={styles.outOfStockText}>Out of Stock</Text>
            </View>
          )}

          {/* Low Stock Indicator */}
          {isLowStock && !isOutOfStock && (
            <View style={styles.lowStockBadge}>
              <Ionicons name="warning" size={12} color={colors.white} />
              <Text style={styles.lowStockText}>Low Stock</Text>
            </View>
          )}

          {/* Variants Indicator */}
          {product.hasVariants && !isOutOfStock && !isLowStock && (
            <View style={styles.variantsBadge}>
              <Ionicons name="layers-outline" size={14} color={colors.white} />
            </View>
          )}
        </View>

        {/* Product Info */}
        <View style={styles.infoContainer}>
          {/* Top Row: Brand/SKU */}
          <View style={styles.topRow}>
            {product.brand && (
              <Text style={styles.brand} numberOfLines={1}>
                {product.brand}
              </Text>
            )}
            {product.sku && (
              <Text style={styles.sku} numberOfLines={1}>
                {product.sku}
              </Text>
            )}
          </View>

          {/* Product Name */}
          <Text style={styles.name} numberOfLines={1}>
            {product.name}
          </Text>

          {/* Description */}
          {product.description && (
            <Text style={styles.description} numberOfLines={1}>
              {product.description}
            </Text>
          )}

          {/* Price Section */}
          <View style={styles.priceSection}>
            <View style={styles.priceContainer}>
              <Text style={styles.price}>
                {getCurrencySymbol(product.currency || businessCurrency())} {displayPrice?.toFixed(2)}
              </Text>
              {hasDiscount && (
                <Text style={styles.originalPrice}>
                  {getCurrencySymbol(product.currency || businessCurrency())} {product.price.toFixed(2)}
                </Text>
              )}
            </View>
            {product.inventoryQuantity !== undefined && product.inventoryQuantity >= 0 && !isOutOfStock && (
              <Text style={styles.inventory}>
                {product.inventoryQuantity} in stock
              </Text>
            )}
          </View>

          {/* Action Buttons Row with Catalogue Info */}
          <View style={styles.actionsRow}>
            {/* Catalogues Info */}
            {product.catalogues && product.catalogues.length > 0 && (
              <View style={styles.cataloguesContainer}>
                <Ionicons name="cube-outline" size={14} color={colors.textLight} style={styles.cataloguesIcon} />
                <Text style={styles.cataloguesText} numberOfLines={1} ellipsizeMode="tail">
                  {product.catalogues[0].name}
                  {product.catalogues.length > 1 && ` and ${product.catalogues.length - 1} more`}
                </Text>
              </View>
            )}
            
            {/* Action Buttons */}
            {showActions && (onEdit || onDelete) && (
              <View style={styles.actionsContainer}>
                {onEdit && (
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={handleEdit}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Ionicons name="create-outline" size={Platform.OS === 'web' ? 18 : 14} color={colors.primary} />
                  </TouchableOpacity>
                )}
                {onDelete && (
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={handleDelete}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Ionicons name="trash-outline" size={Platform.OS === 'web' ? 18 : 14} color={colors.error} />
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    overflow: 'hidden',
    marginBottom: Platform.OS === 'web' ? spacing.sm : spacing.xs,
    borderWidth: 2,
    borderColor: 'transparent',
    ...designTokens.shadows.sm,
  },
  containerSelected: {
    borderColor: colors.primary,
  },
  selectionCheckbox: {
    position: 'absolute',
    top: 4,
    left: 4,
    backgroundColor: 'rgba(0,0,0,0.35)',
    borderRadius: 999,
  },
  content: {
    flexDirection: 'row',
    alignItems: Platform.OS === 'web' ? 'stretch' : 'flex-start',
  },
  imageContainer: {
    width: 100,
    height: 100,
    minHeight: 100,
    maxHeight: 100,
    position: 'relative',
    backgroundColor: colors.lightGray,
    flexShrink: 0,
    flexGrow: 0,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
    alignSelf: 'center',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: withAlpha(colors.primary, 0.13),
  },
  placeholderText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  discountBadge: {
    position: 'absolute',
    top: spacing.xs,
    left: spacing.xs,
    backgroundColor: colors.error,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  discountText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: 'bold',
    fontFamily: typography.fontFamily.bold,
  },
  outOfStockBadge: {
    position: 'absolute',
    top: spacing.xs,
    right: spacing.xs,
    backgroundColor: colors.error,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
    gap: 4,
  },
  outOfStockText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  lowStockBadge: {
    position: 'absolute',
    top: spacing.xs,
    right: spacing.xs,
    backgroundColor: colors.warning || '#FF9800',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
    gap: 4,
  },
  lowStockText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  variantsBadge: {
    position: 'absolute',
    bottom: spacing.xs,
    right: spacing.xs,
    backgroundColor: colors.primary,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContainer: {
    flex: 1,
    padding: Platform.OS === 'web' ? spacing.sm : spacing.md,
    paddingVertical: Platform.OS === 'web' ? spacing.sm : spacing.md,
    justifyContent: 'space-between',
    alignSelf: Platform.OS === 'web' ? 'stretch' : 'flex-start',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Platform.OS === 'web' ? spacing.xs / 2 : 2,
    gap: spacing.xs / 2,
  },
  brand: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
    textTransform: 'uppercase',
    flex: 1,
    lineHeight: 12,
  },
  sku: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'right',
    lineHeight: 12,
  },
  name: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: Platform.OS === 'web' ? spacing.xs / 2 : 2,
    marginTop: Platform.OS === 'web' ? 0 : 2,
    fontFamily: typography.fontFamily.semibold,
    lineHeight: 14,
    includeFontPadding: false,
  },
  description: {
    fontSize: typography.fontSize.xs - 1,
    color: colors.textLight,
    marginBottom: Platform.OS === 'web' ? spacing.xs / 2 : 2,
    marginTop: 0,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 12,
    includeFontPadding: false,
  },
  priceSection: {
    marginTop: Platform.OS === 'web' ? spacing.xs / 2 : 2,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.OS === 'web' ? spacing.xs / 2 : 2,
    gap: spacing.xs,
  },
  price: {
    fontSize: Platform.OS === 'web' ? typography.fontSize.base : typography.fontSize.sm,
    fontWeight: 'bold',
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
    lineHeight: Platform.OS === 'web' ? 18 : 16,
    includeFontPadding: false,
  },
  originalPrice: {
    fontSize: typography.fontSize.xs - 1,
    color: colors.textLight,
    textDecorationLine: 'line-through',
    fontFamily: typography.fontFamily.primary,
    lineHeight: 12,
  },
  inventory: {
    fontSize: typography.fontSize.xs - 1,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: Platform.OS === 'web' ? spacing.xs / 4 : 1,
    lineHeight: 12,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: Platform.OS === 'web' ? spacing.xs / 2 : spacing.xs / 2,
    paddingTop: Platform.OS === 'web' ? spacing.xs / 2 : spacing.xs / 2,
    borderTopWidth: 1,
    borderTopColor: `${String(colors.border)}30`,
    gap: spacing.sm,
    minHeight: Platform.OS === 'web' ? 28 : 24,
  },
  cataloguesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    flex: 1,
    minWidth: 0,
  },
  cataloguesIcon: {
    marginRight: 0,
  },
  cataloguesText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    flex: 1,
    marginLeft: spacing.xs / 2,
    lineHeight: 12,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.xs,
    flexShrink: 0,
  },
  actionButton: {
    width: Platform.OS === 'web' ? 32 : 26,
    height: Platform.OS === 'web' ? 32 : 26,
    borderRadius: Platform.OS === 'web' ? 16 : 13,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
  },
});
